/**
 * AdminDashboardPage Component
 *
 * Admin dashboard for managing lookup tables:
 * - Sidebar with entity list
 * - Main area shows selected entity editor
 * - Manages: Companies, PortCos, Segments, MeasureTypes, SponsorTypes, ScoreCategories, CostCategories
 */

import { Card } from '@medsuite/ui-components';
import { useState, useCallback } from 'react';

import CompaniesEditor from './entities/CompaniesEditor';
import CostCategoriesEditor from './entities/CostCategoriesEditor';
import DigestEditor from './entities/DigestEditor';
import MeasureTypesEditor from './entities/MeasureTypesEditor';
import NegativeImpactCategoriesEditor from './entities/NegativeImpactCategoriesEditor';
import PortCosEditor from './entities/PortCosEditor';
import RoadmapsEditor from './entities/RoadmapsEditor';
import RoadmapThemesEditor from './entities/RoadmapThemesEditor';
import ScoreCategoriesEditor from './entities/ScoreCategoriesEditor';
import SegmentsEditor from './entities/SegmentsEditor';
import SponsorTypesEditor from './entities/SponsorTypesEditor';

type AdminEntity =
  | 'digest'
  | 'companies'
  | 'portcos'
  | 'segments'
  | 'measureTypes'
  | 'sponsorTypes'
  | 'scoreCategories'
  | 'costCategories'
  | 'negativeImpactCategories'
  | 'roadmaps'
  | 'roadmapThemes';

interface EntityConfig {
  id: AdminEntity;
  label: string;
  description: string;
}

const ADMIN_ENTITIES: EntityConfig[] = [
  {
    id: 'digest',
    label: 'Daily Digest',
    description: 'Generate investment activity reports',
  },
  {
    id: 'companies',
    label: 'Companies',
    description: 'Manage company records',
  },
  {
    id: 'portcos',
    label: 'Portfolio Companies',
    description: 'Manage portfolio companies',
  },
  {
    id: 'segments',
    label: 'Segments',
    description: 'Manage business segments',
  },
  {
    id: 'measureTypes',
    label: 'Measure Types',
    description: 'Manage success measure types',
  },
  {
    id: 'sponsorTypes',
    label: 'Sponsor Types',
    description: 'Manage sponsor types (drag to reorder)',
  },
  {
    id: 'scoreCategories',
    label: 'Score Categories',
    description: 'Manage scoring categories',
  },
  {
    id: 'costCategories',
    label: 'Cost Categories',
    description: 'Manage cost categories',
  },
  {
    id: 'negativeImpactCategories',
    label: 'Negative Impact Categories',
    description: 'Manage risk/negative impact categories',
  },
  {
    id: 'roadmaps',
    label: 'Roadmaps',
    description: 'Manage product roadmaps',
  },
  {
    id: 'roadmapThemes',
    label: 'Roadmap Themes',
    description: 'Manage roadmap themes with ordering',
  },
];

export default function AdminDashboardPage() {
  const [selectedEntity, setSelectedEntity] = useState<AdminEntity>('digest');
  const [entityCounts, setEntityCounts] = useState<Record<AdminEntity, number>>({
    digest: 0,
    companies: 0,
    portcos: 0,
    segments: 0,
    measureTypes: 0,
    sponsorTypes: 0,
    scoreCategories: 0,
    costCategories: 0,
    negativeImpactCategories: 0,
    roadmaps: 0,
    roadmapThemes: 0,
  });

  const handleDataLoaded = useCallback((entity: AdminEntity, count: number) => {
    setEntityCounts((prev) => ({ ...prev, [entity]: count }));
  }, []);

  // Create stable callbacks for each entity
  const handleDigestLoaded = useCallback(
    (count: number) => handleDataLoaded('digest', count),
    [handleDataLoaded]
  );
  const handleCompaniesLoaded = useCallback(
    (count: number) => handleDataLoaded('companies', count),
    [handleDataLoaded]
  );
  const handlePortcosLoaded = useCallback(
    (count: number) => handleDataLoaded('portcos', count),
    [handleDataLoaded]
  );
  const handleSegmentsLoaded = useCallback(
    (count: number) => handleDataLoaded('segments', count),
    [handleDataLoaded]
  );
  const handleMeasureTypesLoaded = useCallback(
    (count: number) => handleDataLoaded('measureTypes', count),
    [handleDataLoaded]
  );
  const handleSponsorTypesLoaded = useCallback(
    (count: number) => handleDataLoaded('sponsorTypes', count),
    [handleDataLoaded]
  );
  const handleScoreCategoriesLoaded = useCallback(
    (count: number) => handleDataLoaded('scoreCategories', count),
    [handleDataLoaded]
  );
  const handleCostCategoriesLoaded = useCallback(
    (count: number) => handleDataLoaded('costCategories', count),
    [handleDataLoaded]
  );
  const handleNegativeImpactCategoriesLoaded = useCallback(
    (count: number) => handleDataLoaded('negativeImpactCategories', count),
    [handleDataLoaded]
  );
  const handleRoadmapsLoaded = useCallback(
    (count: number) => handleDataLoaded('roadmaps', count),
    [handleDataLoaded]
  );
  const handleRoadmapThemesLoaded = useCallback(
    (count: number) => handleDataLoaded('roadmapThemes', count),
    [handleDataLoaded]
  );

  const currentCount = entityCounts[selectedEntity];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Administration</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Manage lookup tables and reference data
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex h-[calc(100vh-5rem)]">
        {/* Sidebar */}
        <aside className="w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 overflow-y-auto">
          <nav className="p-4 space-y-1">
            {ADMIN_ENTITIES.map((entity) => (
              <button
                key={entity.id}
                onClick={() => setSelectedEntity(entity.id)}
                className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                  selectedEntity === entity.id
                    ? 'bg-indigo-50 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300 font-medium'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <div className="text-sm font-medium">{entity.label}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {entity.description}
                </div>
              </button>
            ))}
          </nav>
        </aside>

        {/* Main Area */}
        <main className="flex-1 overflow-y-auto p-6">
          <Card>
            <div className="p-6">
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  {ADMIN_ENTITIES.find((e) => e.id === selectedEntity)?.label}
                  <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                    {currentCount} {currentCount === 1 ? 'item' : 'items'}
                  </span>
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {ADMIN_ENTITIES.find((e) => e.id === selectedEntity)?.description}
                </p>
              </div>

              {/* Entity Editors */}
              {selectedEntity === 'digest' && (
                <DigestEditor onDataLoaded={handleDigestLoaded} />
              )}
              {selectedEntity === 'companies' && (
                <CompaniesEditor onDataLoaded={handleCompaniesLoaded} />
              )}
              {selectedEntity === 'portcos' && <PortCosEditor onDataLoaded={handlePortcosLoaded} />}
              {selectedEntity === 'segments' && (
                <SegmentsEditor onDataLoaded={handleSegmentsLoaded} />
              )}
              {selectedEntity === 'measureTypes' && (
                <MeasureTypesEditor onDataLoaded={handleMeasureTypesLoaded} />
              )}
              {selectedEntity === 'sponsorTypes' && (
                <SponsorTypesEditor onDataLoaded={handleSponsorTypesLoaded} />
              )}
              {selectedEntity === 'scoreCategories' && (
                <ScoreCategoriesEditor onDataLoaded={handleScoreCategoriesLoaded} />
              )}
              {selectedEntity === 'costCategories' && (
                <CostCategoriesEditor onDataLoaded={handleCostCategoriesLoaded} />
              )}
              {selectedEntity === 'negativeImpactCategories' && (
                <NegativeImpactCategoriesEditor
                  onDataLoaded={handleNegativeImpactCategoriesLoaded}
                />
              )}
              {selectedEntity === 'roadmaps' && (
                <RoadmapsEditor onDataLoaded={handleRoadmapsLoaded} />
              )}
              {selectedEntity === 'roadmapThemes' && (
                <RoadmapThemesEditor onDataLoaded={handleRoadmapThemesLoaded} />
              )}
            </div>
          </Card>
        </main>
      </div>
    </div>
  );
}
